import { RECENTLY_INSERTED_DAYS, categoryValueLabel } from './libraryFilterOptions.ts';
import { UNCATEGORISED, libraryActiveFilterCount, type LibraryFilterState } from './libraryFilterState.ts';

/**
 * What the Library says when a view comes back empty.
 *
 * The decks page's `deckEmptyState.ts` for this shelf, keeping both of its
 * rules: with exactly ONE axis on the line names that axis's value, with more
 * than one there is no honest single culprit so the chip row above speaks for
 * it, and every filtered state offers the same clear-all the chip row's button
 * calls.
 *
 * IT ALSO CARRIES THE ONE THING SETTLED QUESTION 5 ASKS FOR. The usage registry
 * arrived in Run 8, after the library it counts, so **Recently inserted and
 * Most used are empty on day one and fill in as the library gets used**. An
 * empty rail row is the single most likely way to conclude that a feature is
 * broken, so those two states say what makes the number move rather than
 * merely reporting a zero. They are the reason this file exists at all in a
 * run that was otherwise about a rail and a grid.
 */

export type LibraryEmptyAction = {
  /** `clear` runs the page's clear-all; `new` opens the New item modal. */
  kind: 'clear' | 'new';
  label: string;
};

export type LibraryEmptyStateInfo = {
  /** A stable id, so the headless UI check can name a state without matching
   *  on copy. Rendered as `data-empty`. */
  key: string;
  message: string;
  action: LibraryEmptyAction | null;
};

/** "Clear filter" when exactly one is on: the button must not offer to clear a
 *  set the user can see is a single thing. */
export function clearLabel(count: number): string {
  return count === 1 ? 'Clear filter' : 'Clear filters';
}

const TYPE_SENTENCES: Record<'single' | 'series', string> = {
  single: 'Every item here holds more than one slide.',
  series: 'No item here holds more than one slide yet.',
};

export function libraryEmptyState(filters: LibraryFilterState): LibraryEmptyStateInfo {
  const count = libraryActiveFilterCount(filters);
  const clear = (key: string, message: string): LibraryEmptyStateInfo => ({
    key,
    message,
    action: { kind: 'clear', label: clearLabel(count) },
  });

  if (count === 0) {
    return {
      key: 'none',
      message: 'Nothing saved to the library yet.',
      action: { kind: 'new', label: 'New item' },
    };
  }

  if (count > 1) return clear('many', 'No items match these filters.');

  if (filters.query) return clear('query', `No items match “${filters.query}”.`);
  if (filters.type) return clear('type', TYPE_SENTENCES[filters.type]);
  if (filters.category) {
    // Library items always carry a category, so an empty Uncategorised bucket
    // is good news rather than a barren folder.
    if (filters.category === UNCATEGORISED) return clear('category', 'Every item is filed in a category.');
    return clear('category', `There is nothing in ${categoryValueLabel(filters.category)} yet.`);
  }
  // The two counted rail rows. Both are zero on a studio whose usage registry
  // is younger than its library, so the line says what makes the number move.
  if (filters.used === 'used') {
    return clear(
      'used',
      'No item has been inserted into a deck yet. This count fills in as decks take slides from the library.',
    );
  }
  if (filters.used === 'never') return clear('never-used', 'Every item has been used in at least one deck.');
  if (filters.inserted) {
    return clear(
      'inserted',
      `Nothing has been inserted into a deck in the last ${RECENTLY_INSERTED_DAYS} days. This fills in as decks take slides from the library.`,
    );
  }

  // Unreachable while the axes and the branches above agree; a sentence rather
  // than a throw, because an empty page is not the place to crash.
  return clear('many', 'No items match these filters.');
}
